import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useMemo, useState } from 'react';
import Select from 'react-select';
import { toast } from 'react-toastify';
import type {
  AssignmentDetail,
  AssignmentExpenseCreate,
  AssignmentExpenseList,
  ExpenseCategory,
  PatchedAssignmentExpenseUpdate,
} from '../../../../../js/api';
import { AssignmentsService } from '../../../../../js/api';
import { useCurrencyFormatter } from '../../../../../js/app/hooks/use-currency-formatter';
import { handleApiError } from '../../../../../js/app/utils/errorhandling';
import { Button } from '../../../components/button';
import { Dialog, DialogActions, DialogBody, DialogTitle } from '../../../components/dialog';
import { DOCUMENTS_QUERY_KEY } from '../../../components/documents-tab';
import { ExpenseAndTermsForm } from '../../../components/expense-and-terms-form';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/table';
import { TooltippedText } from '../../../components/tooltipped-text';
import {
  formatDate,
  formatDateTo24Hr,
  formDateToApiDateTime,
  parseFormDate,
} from '../../../utils/date';
import { reactSelectReset } from '../../../utils/utils';
import { useAudience } from '../../../utils/workflowAccess';
import { ExpenseDocumentsModal } from './ExpenseDocumentsModal';
import { ExpenseHistoryModal } from './ExpenseHistoryModal';
import {
  ASSIGNMENT_EXPENSES_QUERY_KEY,
  ASSIGNMENT_QUERY_KEY,
  EXPENSE_CATEGORIES_QUERY_KEY,
} from './queryKeys';

function formatCurrency(value: string | number | null | undefined): string {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (num == null || isNaN(num)) return '--';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(num);
}

type ExpenseModalMode = 'add' | 'edit';

const ExpenseModal: React.FC<{
  open: boolean;
  onClose: () => void;
  mode: ExpenseModalMode;
  expense?: AssignmentExpenseList;
  categories: ExpenseCategory[];
  assignmentId: string;
  onSuccess: () => void;
}> = ({ open, onClose, mode, expense, categories, assignmentId, onSuccess }) => {
  const [categoryId, setCategoryId] = useState('');
  const [amount, setAmount] = useState('');
  const [expenseAt, setExpenseAt] = useState('');
  const [comment, setComment] = useState('');

  React.useEffect(() => {
    if (open) {
      if (mode === 'edit' && expense) {
        setCategoryId(expense.expense_category.id);
        setAmount(expense.amount);
        setComment(expense.comment || '');
      } else {
        setCategoryId(categories.length === 1 ? categories[0].id : '');
        setAmount('');
        setExpenseAt(parseFormDate(new Date()) || '');
        setComment('');
      }
    }
  }, [open, mode, expense, categories]);

  const createMutation = useMutation({
    mutationFn: (data: AssignmentExpenseCreate) =>
      AssignmentsService.assignmentsAssignmentExpensesCreate({
        id: assignmentId,
        requestBody: data,
      }),
    onSuccess: () => {
      toast.success('Expense added');
      onSuccess();
      onClose();
    },
    onError: (error) => handleApiError(error),
  });

  const updateMutation = useMutation({
    mutationFn: (data: PatchedAssignmentExpenseUpdate) =>
      AssignmentsService.assignmentsAssignmentExpensePartialUpdate({
        id: expense?.id || '',
        requestBody: data,
      }),
    onSuccess: () => {
      toast.success('Expense updated');
      onSuccess();
      onClose();
    },
    onError: (error) => handleApiError(error),
  });

  const isPending = createMutation.isLoading || updateMutation.isLoading;
  const parsedAmount = parseFloat(amount);
  const isValid = (mode === 'edit' || !!categoryId) && !isNaN(parsedAmount) && parsedAmount >= 0;

  const handleSubmit = () => {
    if (!isValid) return;
    if (mode === 'add') {
      createMutation.mutate({
        expense_category: categoryId,
        amount,
        expense_at: formDateToApiDateTime(expenseAt),
        comment: comment || undefined,
      });
    } else {
      updateMutation.mutate({
        amount,
        comment: comment || undefined,
      });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} size="lg">
      <DialogTitle>{mode === 'add' ? 'Add Expense' : 'Edit Expense'}</DialogTitle>
      <DialogBody>
        <div className="tw:space-y-4">
          <div>
            <label
              htmlFor="category_title"
              className="tw:block tw:text-sm tw:font-medium tw:text-zinc-700 tw:mb-1"
            >
              Category
            </label>
            {mode === 'edit' ? (
              <div id="category_title" className="tw:text-sm tw:text-zinc-600 tw:py-2">
                {expense?.expense_category.title}
              </div>
            ) : (
              <Select
                options={categories.map((cat) => ({ value: cat.id, label: cat.title }))}
                value={
                  categoryId
                    ? {
                        value: categoryId,
                        label: categories.find((c) => c.id === categoryId)?.title || '',
                      }
                    : null
                }
                onChange={(option) => setCategoryId(option?.value || '')}
                placeholder="Select category..."
                className="tw:w-full"
                styles={reactSelectReset}
                isClearable={false}
              />
            )}
          </div>
          <div>
            <label
              htmlFor="amount"
              className="tw:block tw:text-sm tw:font-medium tw:text-zinc-700 tw:mb-1"
            >
              Amount
            </label>
            <input
              id="amount"
              type="number"
              min="0"
              step="0.01"
              className="tw:w-full tw:rounded-lg tw:border tw:border-zinc-300 tw:px-3 tw:py-2 tw:text-sm tw:focus:border-brand-green-1 tw:focus:ring-1 tw:focus:ring-brand-green-1 tw:outline-none"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
            />
          </div>
          {mode === 'add' && (
            <div>
              <label
                htmlFor="expense_at"
                className="tw:block tw:text-sm tw:font-medium tw:text-zinc-700 tw:mb-1"
              >
                Expense Date
              </label>
              <input
                id="expense_at"
                type="date"
                className="tw:w-full tw:rounded-lg tw:border tw:border-zinc-300 tw:px-3 tw:py-2 tw:text-sm tw:focus:border-brand-green-1 tw:focus:ring-1 tw:focus:ring-brand-green-1 tw:outline-none"
                value={expenseAt}
                onChange={(e) => setExpenseAt(e.target.value)}
              />
            </div>
          )}
          <div>
            <label
              htmlFor="comment"
              className="tw:block tw:text-sm tw:font-medium tw:text-zinc-700 tw:mb-1"
            >
              Comment
            </label>
            <textarea
              id="comment"
              className="tw:w-full tw:rounded-lg tw:border tw:border-zinc-300 tw:px-3 tw:py-2 tw:text-sm tw:placeholder-zinc-400 tw:focus:border-brand-green-1 tw:focus:ring-1 tw:focus:ring-brand-green-1 tw:outline-none"
              rows={2}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Optional comment..."
            />
          </div>
        </div>
      </DialogBody>
      <DialogActions>
        <Button plain onClick={onClose} disabled={isPending}>
          Cancel
        </Button>
        <Button color="brandgreen0" onClick={handleSubmit} disabled={!isValid || isPending}>
          {isPending ? 'Saving...' : mode === 'add' ? 'Add' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const ExpensesTab: React.FC<{
  assignment: AssignmentDetail;
  canEdit?: boolean;
  ownerId?: string;
  relatedProviderId?: string;
}> = ({ assignment, canEdit = false, ownerId = '', relatedProviderId }) => {
  const queryClient = useQueryClient();
  const audience = useAudience();
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<ExpenseModalMode>('add');
  const [editingExpense, setEditingExpense] = useState<AssignmentExpenseList | undefined>();
  const [historyExpense, setHistoryExpense] = useState<AssignmentExpenseList | undefined>();
  const [documentsExpense, setDocumentsExpense] = useState<AssignmentExpenseList | undefined>();

  // Fetch expense categories (cached globally)
  const { data: allCategories = [] } = useQuery({
    queryKey: [EXPENSE_CATEGORIES_QUERY_KEY],
    queryFn: () => AssignmentsService.assignmentsExpenseCategoryList(),
    staleTime: 5 * 60 * 1000,
  });

  // Filter categories by audience
  const editableCategories = useMemo(
    () => allCategories.filter((cat) => cat.allowed_audience_edit?.includes(audience)),
    [allCategories, audience]
  );
  const editableCategoryCodes = useMemo(
    () => new Set(editableCategories.map((c) => c.code)),
    [editableCategories]
  );

  const canManageExpenses = canEdit && editableCategories.length > 0;

  // Fetch expenses
  const { data: expensesRaw } = useQuery({
    queryKey: [ASSIGNMENT_EXPENSES_QUERY_KEY, assignment.id],
    queryFn: () => AssignmentsService.assignmentsAssignmentExpensesList({ id: assignment.id }),
    enabled: !!assignment.id,
  });

  const expenses: AssignmentExpenseList[] = useMemo(() => {
    if (!expensesRaw) return [];
    if (Array.isArray(expensesRaw)) return expensesRaw;
    if ('results' in expensesRaw) return (expensesRaw as any).results || [];
    return [expensesRaw as AssignmentExpenseList];
  }, [expensesRaw]);

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => AssignmentsService.assignmentsAssignmentExpenseDestroy({ id }),
    onSuccess: () => {
      toast.success('Expense removed');
      invalidate();
    },
    onError: (error) => handleApiError(error),
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: [ASSIGNMENT_EXPENSES_QUERY_KEY, assignment.id] });
    queryClient.invalidateQueries({ queryKey: [ASSIGNMENT_QUERY_KEY, assignment.id] });
    // Bonus expenses feed the ACL's totals — the edit may have replaced the
    // letter, so the documents tab must refetch.
    queryClient.invalidateQueries({ queryKey: [DOCUMENTS_QUERY_KEY, assignment.id] });
  };

  const handleDelete = (expense: AssignmentExpenseList) => {
    if (
      window.confirm(
        `Remove ${expense.expense_category.title} expense of ${formatCurrency(expense.amount)}?`
      )
    ) {
      deleteMutation.mutate(expense.id);
    }
  };

  // Totals
  const total = expenses.reduce((sum, e) => sum + parseFloat(e.amount || '0'), 0);
  const { formatCurrencyPostfix } = useCurrencyFormatter({ postfix: '', placeholder: '-' });
  return (
    <div className="tw:space-y-6">
      <section>
        {/* Add Expense button */}
        {canManageExpenses && (
          <div className="tw:flex tw:justify-between tw:items-center tw:mb-2">
            {assignment.total_bonus_amount && Number(assignment.total_bonus_amount) > 0 ? (
              <div>
                <span className="tw:text-zinc-600 tw:text-xs tw:mr-1">Shift Bonus</span>
                {formatCurrencyPostfix(assignment.total_bonus_amount)}
              </div>
            ) : (
              <div />
            )}
            <Button
              small
              color="brand-green-outline"
              onClick={() => {
                setEditingExpense(undefined);
                setModalMode('add');
                setModalOpen(true);
              }}
            >
              <i className="fas fa-plus tw:mr-1" />
              Add Expense
            </Button>
          </div>
        )}

        {expenses.length === 0 ? (
          <div className="tw:rounded-md tw:border tw:border-dashed tw:border-zinc-300 tw:dark:border-zinc-700 tw:p-6 tw:text-center tw:text-sm tw:text-zinc-500">
            No expenses recorded
          </div>
        ) : (
          <Table compact striped>
            <TableHead>
              <TableHeader>
                <span />
              </TableHeader>
              <TableHeader>Date</TableHeader>
              <TableHeader>Category</TableHeader>
              <TableHeader className="tw:!text-right">Amount</TableHeader>
              <TableHeader>Comment</TableHeader>
            </TableHead>
            <TableBody>
              {expenses.map((expense) => {
                const canEditThis =
                  canManageExpenses && editableCategoryCodes.has(expense.expense_category.code);
                const expenseTooltipContent = (
                  <div className="tw:flex tw:flex-col tw:items-start">
                    <div className="tw:text-[12px] tw:font-medium">
                      Created By {expense.created_by_name}
                    </div>
                    <div className="tw:text-[12px] tw:font-medium">
                      {formatDateTo24Hr(expense.created_at)}
                    </div>
                  </div>
                );
                return (
                  <TableRow key={expense.id}>
                    <TableCell>
                      <div className="tw:flex tw:items-center tw:gap-2">
                        <button
                          type="button"
                          className="tw:text-zinc-400 tw:hover:text-zinc-600 tw:cursor-pointer tw:bg-transparent tw:border-0 tw:p-0"
                          onClick={() => setHistoryExpense(expense)}
                          title="View expense history"
                        >
                          <i className="fas fa-clock-rotate-left tw:text-sm" />
                        </button>
                        <button
                          type="button"
                          className="tw:text-zinc-400 tw:hover:text-zinc-600 tw:cursor-pointer tw:bg-transparent tw:border-0 tw:p-0 tw:relative"
                          onClick={() => setDocumentsExpense(expense)}
                          title="Expense documents"
                        >
                          <i className="fas fa-paperclip tw:text-sm" />
                          {(expense.document_count ?? 0) > 0 && (
                            <span className="tw:absolute tw:-top-1.5 tw:-right-2 tw:bg-brand-green-0 tw:text-white tw:text-[9px] tw:leading-none tw:font-medium tw:rounded-full tw:min-w-[14px] tw:h-[14px] tw:flex tw:items-center tw:justify-center tw:px-0.5">
                              {expense.document_count}
                            </span>
                          )}
                        </button>
                        {canEditThis && (
                          <>
                            <button
                              type="button"
                              className="tw:text-zinc-400 tw:hover:text-brand-green-0 tw:cursor-pointer tw:bg-transparent tw:border-0 tw:p-0"
                              onClick={() => {
                                setEditingExpense(expense);
                                setModalMode('edit');
                                setModalOpen(true);
                              }}
                              title="Edit expense"
                            >
                              <i className="fas fa-pencil tw:text-sm" />
                            </button>
                            <button
                              type="button"
                              className="tw:text-zinc-400 tw:hover:text-stoplight-danger tw:cursor-pointer tw:bg-transparent tw:border-0 tw:p-0"
                              onClick={() => handleDelete(expense)}
                              title="Remove expense"
                            >
                              <i className="fas fa-xmark tw:text-sm" />
                            </button>
                          </>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="tw:whitespace-nowrap">
                      {formatDate(expense.expense_at)}
                    </TableCell>
                    <TableCell>
                      {expense.expense_category.title}{' '}
                      <TooltippedText tooltipContent={expenseTooltipContent}>
                        <i className="far fa-circle-info tw:text-blue-700" />
                      </TooltippedText>
                    </TableCell>
                    <TableCell className="tw:text-right">
                      {formatCurrency(expense.amount)}
                    </TableCell>
                    <TableCell className="tw:text-zinc-500 tw:text-xs">
                      {expense.comment && expense.comment.length > 30 ? (
                        <>
                          {expense.comment.slice(0, 26)}...{' '}
                          <TooltippedText
                            tooltipContent={expense.comment}
                            tooltipClassName="tw:max-w-[300px]"
                          >
                            <span className="tw:underline tw:cursor-default tw:text-blue-700">
                              more
                            </span>
                          </TooltippedText>
                        </>
                      ) : (
                        expense.comment || ''
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
              {/* Totals row */}
              <TableRow>
                <TableCell />
                <TableCell />
                <TableCell className="tw:font-semibold">Total</TableCell>
                <TableCell className="tw:text-right tw:font-semibold">
                  {formatCurrency(total)}
                </TableCell>
                <TableCell />
              </TableRow>
            </TableBody>
          </Table>
        )}
      </section>

      <ExpenseAndTermsForm
        entity={assignment}
        entityType="assignment"
        onUpdate={() => {}}
        sectionLabel="Expected Expense Types & Terms"
      />

      {canManageExpenses && (
        <ExpenseModal
          open={modalOpen}
          onClose={() => {
            setModalOpen(false);
            setEditingExpense(undefined);
          }}
          mode={modalMode}
          expense={editingExpense}
          categories={editableCategories}
          assignmentId={assignment.id}
          onSuccess={invalidate}
        />
      )}

      <ExpenseHistoryModal
        open={!!historyExpense}
        onClose={() => setHistoryExpense(undefined)}
        expense={historyExpense}
      />

      <ExpenseDocumentsModal
        open={!!documentsExpense}
        onClose={() => {
          setDocumentsExpense(undefined);
          invalidate();
        }}
        expense={documentsExpense}
        ownerId={ownerId}
        relatedProviderId={relatedProviderId}
      />
    </div>
  );
};
